import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, Typography, Alert, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import Loader from '../../common/components/Loader';
import { useGlobalStore } from '../../application/store/store';
import { NavigationRoutes } from '../../application/router/routes';
import DeleteMessageDialog from './components/DeleteMessageDialog';

const MessageDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {
        currentMessage,
        messagesLoading,
        messagesError,
        fetchMessage,
        deleteMessage,
        clearMessagesError,
        resetMessages,
    } = useGlobalStore();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (id) {
            fetchMessage(id);
        }

        return () => {
            resetMessages();
        };
    }, [id]);

    const handleBack = () => {
        navigate(NavigationRoutes.messages);
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!id) return;

        setIsDeleting(true);
        try {
            await deleteMessage(id);
            setDeleteDialogOpen(false);
            navigate(NavigationRoutes.messages);
        } catch (error) {
            console.error('Error deleting message:', error);
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    if (messagesLoading && !currentMessage) {
        return <Loader />;
    }

    if (messagesError && !currentMessage) {
        return (
            <div className="p-3">
                <Alert severity="error">{messagesError}</Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mt: 2 }}
                >
                    Tillbaka till listan
                </Button>
            </div>
        );
    }

    if (!currentMessage) {
        return (
            <div className="p-3">
                <Alert severity="warning">Meddelandet hittades inte</Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mt: 2 }}
                >
                    Tillbaka till listan
                </Button>
            </div>
        );
    }

    return (
        <div className="p-3">
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
                    Tillbaka till listan
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClick}
                >
                    Ta bort
                </Button>
            </Box>

            {messagesError && (
                <Alert
                    severity="error"
                    onClose={clearMessagesError}
                    sx={{ mb: 2 }}
                >
                    {messagesError}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {currentMessage.title}
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}
                >
                    <Box
                        display="grid"
                        gridTemplateColumns="auto 1fr"
                        gap={1}
                        sx={{ '& > *:nth-of-type(odd)': { fontWeight: 500 } }}
                    >
                        <Typography variant="body2">Från:</Typography>
                        <Typography variant="body2">
                            {currentMessage.createdByName}
                        </Typography>

                        <Typography variant="body2">Datum:</Typography>
                        <Typography variant="body2">
                            {new Date(currentMessage.createdOn).toLocaleString(
                                'sv-SE'
                            )}
                        </Typography>

                        <Typography variant="body2">Läst av:</Typography>
                        <Typography variant="body2">
                            {currentMessage.readCount} av{' '}
                            {currentMessage.totalUserCount} användare
                        </Typography>
                    </Box>
                </Paper>

                <Divider sx={{ mb: 3 }} />

                <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
                >
                    {currentMessage.content}
                </Typography>
            </Paper>

            <DeleteMessageDialog
                open={deleteDialogOpen}
                message={
                    currentMessage
                        ? {
                              id: currentMessage.id,
                              title: currentMessage.title,
                              createdByName: currentMessage.createdByName,
                              createdOn: currentMessage.createdOn,
                              readCount: currentMessage.readCount,
                              totalUserCount: currentMessage.totalUserCount,
                          }
                        : null
                }
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default MessageDetailPage;
